/**
 * InterviewStage.jsx — DEAD SIGNAL vertical slice
 *
 * The interview overlay, rendered while interview.active. Three pieces:
 *   - HargroveBioscanHUD: clickable metric rows that generate observation
 *     text routed through the interference gate.
 *   - CraineBioscanHUD: passive handler readout (stress, composure,
 *     assessment). On interference_conflict, Craine stays STEADY.
 *   - SignalDisruptedOverlay: single vague line ("LINK DISRUPTED"),
 *     dim styling, 1200ms auto-fade.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useGameStore } from '../state/gameState.js';
import { BIOSCAN_OBSERVATIONS } from '../systems/bioscan.js';

// Topic → BIO-SCAN thread label. A HUD cue (what's on the table right now),
// never an interpretation of what the readings "mean."
const THREAD_LABEL = {
  default: 'BASELINE',
  courier_manifest: 'COURIER MANIFEST',
  chip_donation: 'CHIP DONATION',
  clinical_trial_st_erasmus: 'ST. ERASMUS · CLINICAL TRIAL',
  celeste_grief: 'CELESTE · GRIEF',
  unprompted_denial: 'UNPROMPTED DENIAL',
  interference_conflict: 'SIGNAL CONFLICT',
};

// Resting baseline (the `default` row). Per-metric deviation cues compare to it.
const BASELINE = { heartRate: 74, skinTempF: 98.4 };

function perspirationCue(v) {
  const s = String(v || '').toLowerCase();
  if (s === 'none') return '— none';
  if (s.includes('moderate')) return '▲▲ elevated';
  if (s.includes('trace')) return '▲ onset';
  return '▲';
}

// Each metric renders a value column plus a short deviation cue (the HUD tell).
const METRIC_KEYS = [
  {
    key: 'heartRate',
    label: 'Heart rate',
    value: (r, prevHR) =>
      prevHR != null && prevHR !== r.heartRate
        ? `${prevHR} → ${r.heartRate} bpm`
        : `${r.heartRate} bpm`,
    cue: (r, prevHR) => {
      const base = prevHR != null ? prevHR : BASELINE.heartRate;
      const d = r.heartRate - base;
      if (d > 10) return `▲ SPIKE +${d}`;
      if (d > 0) return `▲ +${d}`;
      if (d < 0) return `▼ ${d}`;
      return '— stable';
    },
  },
  {
    key: 'skinTemp',
    label: 'Skin temp',
    value: (r) => `${r.skinTempF}°F / ${r.skinTemp}°C`,
    cue: (r) => {
      const d = +(r.skinTempF - BASELINE.skinTempF).toFixed(1);
      if (d >= 1.0) return `▲▲ +${d}`;
      if (d >= 0.3) return `▲ +${d}`;
      if (d <= -0.3) return `▼ ${d}`;
      return '— base';
    },
  },
  {
    key: 'perspiration',
    label: 'Perspiration',
    value: (r) => r.perspiration,
    cue: (r) => perspirationCue(r.perspiration),
  },
  {
    key: 'microExpr',
    label: 'Micro-expr',
    value: (r) => r.microExpr,
    cue: () => null, // micro-expr carries its own bracketed [TELL]
  },
];

export function HargroveBioscanHUD({ row, onBioscanClick, contradiction }) {
  const prevHeartRate = useGameStore((s) => s.interview._prevHeartRate);
  const [corruptedKey, setCorruptedKey] = useState(null);
  const corruptTimer = useRef(null);

  const handleClick = useCallback((metricKey) => {
    if (!row || !onBioscanClick) return;
    const gen = BIOSCAN_OBSERVATIONS[metricKey];
    if (!gen) return;
    const text = gen(row);
    const result = onBioscanClick(text);
    if (result?.suppressed) {
      setCorruptedKey(metricKey);
      clearTimeout(corruptTimer.current);
      corruptTimer.current = setTimeout(() => setCorruptedKey(null), 600);
    }
  }, [row, onBioscanClick]);

  useEffect(() => () => clearTimeout(corruptTimer.current), []);

  if (!row) {
    return (
      <div className="bioscan-hud bioscan-hud-hargrove">
        <div className="bioscan-hud-head">
          <span>BIO-SCAN // SUBJECT: Hargrove, V.</span>
          <span className="bioscan-status standby">STANDBY</span>
        </div>
        <div className="bioscan-hud-body">Awaiting first transmission…</div>
      </div>
    );
  }

  const thread = THREAD_LABEL[row.topic] || THREAD_LABEL.default;

  return (
    <div className={`bioscan-hud bioscan-hud-hargrove topic-${row.topic}`}>
      <div className="bioscan-hud-head">
        <span>BIO-SCAN // SUBJECT: Hargrove, V.</span>
        <span className="bioscan-status live">● LIVE</span>
      </div>
      <div className="bioscan-thread">
        <span className="bt-key">THREAD</span>
        <span className="bt-val">{thread}</span>
      </div>
      <div className="bioscan-metrics">
        {METRIC_KEYS.map(({ key, label, value, cue }) => {
          const cueText = cue(row, prevHeartRate);
          return (
            <button
              key={key}
              className={`bioscan-metric-btn ${corruptedKey === key ? 'corrupted' : ''}`}
              onClick={() => handleClick(key)}
              type="button"
            >
              <span className="bm-label">{label}</span>
              <span className="bm-dots" />
              <span className="bm-value">{value(row, prevHeartRate)}</span>
              {cueText && <span className="bm-cue">{cueText}</span>}
            </button>
          );
        })}
      </div>
      <div className="bioscan-readline">
        <span className="br-key">ASSESS</span>
        <span className="br-val">{row.assessment}</span>
      </div>
      {row.pattern && (
        <div className="bioscan-readline">
          <span className="br-key">PATTERN</span>
          <span className="br-val">{row.pattern}</span>
        </div>
      )}
      {row.classification && (
        <div className={`bioscan-classification classification-${row.classification.level.toLowerCase()}`}>
          <div className="bc-level">▓▓ {row.classification.level} ▓▓</div>
          <div className="bc-code">
            {row.classification.status
              ? `${row.classification.bucket} [${row.classification.status}]`
              : row.classification.bucket}
          </div>
        </div>
      )}
      {contradiction && <PatternConflictHUD contradiction={contradiction} />}
    </div>
  );
}

export function PatternConflictHUD({ contradiction }) {
  if (!contradiction) return null;
  return (
    <div className="pattern-conflict-hud">
      <div className="pc-head">PATTERN CONFLICT ━━━━━━━━━━━━━━━━━━━━━━━━━━━</div>
      <div className="pc-claim">Subject claims: "{contradiction.subjectClaim}"</div>
      <div className="pc-evidence">Evidence [{contradiction.evidenceId}]: {contradiction.conflictDescription}</div>
      <div className="pc-assessment">Assessment: Statement contradicts documented evidence.</div>
      <div className="bioscan-classification classification-confidential">
        <div className="bc-level">▓▓ CONFIDENTIAL ▓▓</div>
        <div className="bc-code">{contradiction.bucket} [{contradiction.status || 'routing suspended'}]</div>
      </div>
    </div>
  );
}

function SignalDisruptedOverlay({ notice }) {
  const [visible, setVisible] = useState(false);

  const duration = notice?.level ? 4500 : 3000;

  useEffect(() => {
    if (notice) {
      setVisible(true);
      const t = setTimeout(() => setVisible(false), duration);
      return () => clearTimeout(t);
    }
    setVisible(false);
  }, [notice, duration]);

  if (!visible || !notice) return null;

  // Classification notice (Phase 2)
  if (notice.level) {
    return (
      <div
        className="signal-disrupted-overlay classification-notice"
        style={{ animation: `sd-fade ${duration}ms ease-out forwards` }}
      >
        <div className="sd-banner classification-flash">▓▓ {notice.level} ▓▓</div>
        {notice.code ? <div className="sd-code">{notice.code}</div> : null}
      </div>
    );
  }

  // Legacy suppression notice (fallback)
  const label = notice.failed || 'LINK DISRUPTED';

  return (
    <div
      className="signal-disrupted-overlay"
      style={{ animation: `sd-fade ${duration}ms ease-out forwards` }}
    >
      <div className="sd-banner glitch-text">{label}</div>
    </div>
  );
}

export default function InterviewStage() {
  const active = useGameStore((s) => s.interview.active);
  const notice = useGameStore((s) => s.transmissionNotice);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (notice) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 380);
      return () => clearTimeout(t);
    }
  }, [notice]);

  if (!active) return null;

  return (
    <div className={`interview-stage ${flash ? 'tx-flash' : ''}`}>
      <SignalDisruptedOverlay notice={notice} />
    </div>
  );
}
